/**
 * Route → feature gating configuration.
 *
 * Maps account/admin routes to required features + behavior when access is
 * denied. Used by:
 *   - server layouts (call `requirePageAccess` in the layout)
 *   - sidebar/nav rendering (filter routes the user can't see)
 *
 * When adding a new gated page:
 *   1. Add the feature to _docs/feature-catalog.csv (regenerate catalog)
 *   2. Add an entry here pointing route → feature key
 *   3. Wrap the page layout with `requirePageAccess(feature)` or rely on middleware
 */

import type { OwnerType } from "./types";

export interface RouteGate {
  /** Route pattern — matched as prefix against request path. */
  pattern: string;
  /** Feature key that gates this route. */
  feature: string;
  /** Owner scope to check against (defaults to feature's scope if omitted). */
  scope?: OwnerType;
  /**
   * Behavior when access is denied:
   *  - `redirect_upgrade` — send user to pricing page
   *  - `teaser`           — render the page with overlay + CTA
   *  - `hide_from_nav`    — filter route out of sidebar, still accessible if URL known
   *  - `block_404`        — return 404 Not Found
   */
  onBlock: "redirect_upgrade" | "teaser" | "hide_from_nav" | "block_404";
}

/**
 * Order matters for prefix matching — place more-specific patterns first.
 */
/**
 * Patterns may contain `:id` segments (dynamic route params) — matched as a
 * single non-slash segment. Prefix matching on the remainder still applies.
 *   e.g. `/account/facility/:id/hr` matches `/account/facility/abc-123/hr`.
 */
export const ROUTE_GATES: RouteGate[] = [
  // ─── Clinic (facility scope) ────────────────────────────────────────
  {
    pattern: "/account/facility/:id/hr",
    feature: "facility.hr.shift_scheduling",
    onBlock: "teaser",
  },
  {
    pattern: "/account/facility/:id/inventory",
    feature: "facility.inventory.stock",
    onBlock: "teaser",
  },
  {
    pattern: "/account/facility/:id/api",
    feature: "facility.api.access",
    onBlock: "redirect_upgrade",
  },
  {
    pattern: "/account/facility/:id/reports/custom",
    feature: "facility.analytics.custom_reports",
    onBlock: "redirect_upgrade",
  },
  {
    pattern: "/account/facility/:id/branches",
    feature: "facility.chain.dashboard_kpi",
    onBlock: "teaser",
  },
  {
    pattern: "/account/facility/:id/crm/campaigns",
    feature: "facility.crm.campaigns.per_month",
    onBlock: "redirect_upgrade",
  },
  {
    pattern: "/account/facility/:id/documents",
    feature: "facility.docs.templates",
    onBlock: "teaser",
  },

  // ─── Provider scope ──────────────────────────────────────────────────
  {
    pattern: "/account/provider/analytics/benchmark",
    feature: "provider.analytics.benchmark",
    onBlock: "redirect_upgrade",
  },
  {
    pattern: "/account/provider/api-keys",
    feature: "provider.api_access.calls_per_month",
    onBlock: "redirect_upgrade",
  },
  {
    pattern: "/account/provider/medical-journal",
    feature: "provider.medical_journal",
    onBlock: "teaser",
  },
  {
    pattern: "/account/provider/team",
    feature: "provider.team.staff_invites",
    onBlock: "redirect_upgrade",
  },

  // ─── User (patient) scope ──────────────────────────────────────────
  // NOTE: user scope has a legacy limit system in src/lib/subscriptions/feature-gate.ts
  // that's still in use. New user-scope gates go here; migrate old ones over time.
  // Routes below are forward-looking — pages not yet built.
  {
    pattern: "/account/user/ai-assistant",
    feature: "user.ai_assistant_personal.per_month",
    onBlock: "teaser",
  },
];

/**
 * Compile a pattern like `/account/facility/:id/hr` into a regex that matches
 * the exact route or its sub-paths, treating `:segment` as a single non-slash
 * path segment.
 */
function patternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .split("/")
    .map((seg) => {
      if (seg.startsWith(":")) return "[^/]+";
      return seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("/");
  // Match exact + allow trailing path segments
  return new RegExp(`^${escaped}(?:/.*)?$`);
}

const COMPILED: Array<{ gate: RouteGate; regex: RegExp }> = ROUTE_GATES.map((gate) => ({
  gate,
  regex: patternToRegex(gate.pattern),
}));

/**
 * Find the gate that applies to a given pathname.
 * Returns null if the route is public / ungated.
 */
export function findRouteGate(pathname: string): RouteGate | null {
  // Strip locale + country prefix (e.g. "/ro/ro/account/..." → "/account/...")
  const stripped = pathname.replace(/^\/[a-z]{2}\/[a-z]{2}(?=\/|$)/, "");
  for (const { gate, regex } of COMPILED) {
    if (regex.test(stripped)) return gate;
  }
  return null;
}
